import { Component, OnDestroy, OnInit } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef, DialogService } from 'primeng/dynamicdialog';
import { Observable, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import _ from 'lodash';

import { Account } from '../../models/account';
import { CmsSiteService } from '../../models/content';
import { AccountService } from '../../services/account.service';
import { ModalService } from '../../services/modal.service';
import { PicalunaSiteService } from '../../services/picaluna-site.service';
import { ContentSiteServiceComponent } from './content-site-service.component';

/**
 * Website services list (SERVICES-CMS.md §3) — the slim data records behind
 * the service pages: price, inclusions, per-service FAQs. Page LAYOUT is
 * edited in Pages (each service page is composed from service-hero /
 * service-inclusions / service-faqs blocks that pull from these records).
 *
 * One edit here reaches every derived surface on the next publish: the
 * page's meta title ({price:<id>} token), the hero price button, the
 * schema, and the home page's service cards.
 */
@Component({
  selector: 'app-content-site-service-list',
  templateUrl: './content-site-service-list.component.html',
})
export class ContentSiteServiceListComponent implements OnInit, OnDestroy {
  accountLoading = true;
  loadFailed = false;

  private unsubscribe$ = new Subject<void>();
  account$: Observable<Account>;

  publishingId: string | null = null;

  constructor(
    private modalService: ModalService,
    private accountService: AccountService,
    private dialogService: DialogService,
    private picalunaSiteService: PicalunaSiteService,
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig
  ) {
    this.account$ = this.accountService.account$;
  }

  ngOnInit(): void {
    this.accountService.ensureFullAccount().subscribe({
      next: () => (this.accountLoading = false),
      error: (err) => {
        this.accountLoading = false;
        this.loadFailed = true;
        this.modalService.error(err?.error?.error || err);
      },
    });
  }
  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  servicesAsList(content: any): CmsSiteService[] {
    const data = content?.siteServices;
    if (!data) return [];
    const list = (Array.isArray(data) ? data.filter(Boolean) : Object.values(data).filter(Boolean)) as CmsSiteService[];
    return _.sortBy(list, (s) => s?.order ?? 99);
  }

  price(svc: CmsSiteService): string {
    return typeof svc.startingPrice === 'number' ? `$${svc.startingPrice.toLocaleString()}` : '—';
  }

  onAdd(): void {
    const seed: CmsSiteService = {
      _meta: { id: '', index: 0 },
      name: '',
      category: 'cremation',
      published: true,
      inclusions: [],
      faqs: [],
    };
    this.openEditor(seed, true);
  }

  onEdit(svc: CmsSiteService): void {
    this.openEditor(_.cloneDeep(svc), false);
  }

  onDelete(svc: CmsSiteService): void {
    const modal = this.modalService.generic({
      title: 'Remove service record',
      copy: [
        `Remove "${svc.name}"? Any page block or {price:${svc._meta.id}} token still referencing it will BLOCK the next publish (with an error naming the page) until the page is updated. Update the page first.`,
      ],
      buttons: [
        { label: 'Cancel', key: 'cancel', class: '' },
        { label: 'Remove', key: 'confirm', class: 'p-button-danger', icon: 'pi pi-trash' },
      ],
    });
    modal.onClose.subscribe({
      next: (reason: string) => {
        if (reason?.toString().toLowerCase() !== 'confirm') return;
        this.accountService
          .removeContentItem('siteServices', svc._meta.id)
          .pipe(take(1))
          .subscribe({ error: (err) => this.modalService.error(err?.error?.error || err) });
      },
    });
  }

  onPublish(svc: CmsSiteService): void {
    const id = svc._meta.id;
    if (!id || this.publishingId) return;

    const modal = this.modalService.generic({
      title: 'Publish this service data?',
      copy: [
        `This publishes "${svc.name}" (price, inclusions, FAQs) to the website preview.`,
        'Every page that renders it updates — meta title, price button, home page card — but no page wording changes.',
      ],
      buttons: [
        { label: 'Cancel', key: 'cancel', class: 'p-button-text' },
        { label: 'Publish', key: 'publish', class: 'p-button-success' },
      ],
    });

    modal?.onClose.pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: (key: string) => {
        if (key !== 'publish') return;
        this.publishingId = id;
        this.picalunaSiteService
          .publish(`services:${id}`)
          .pipe(take(1))
          .subscribe({
            next: () => {
              this.publishingId = null;
              this.modalService.generic({
                title: 'Publishing started',
                copy: [
                  `"${svc.name}" is being published. It takes a few minutes.`,
                  'Track progress on the Website page, then publish to production from there when you are happy with the preview.',
                ],
                buttons: [{ label: 'Close', key: 'close', class: 'p-button-text' }],
              });
            },
            error: (err) => {
              this.publishingId = null;
              this.modalService.error(err?.error?.error || err);
            },
          });
      },
    });
  }

  private openEditor(svc: CmsSiteService, isNew: boolean): void {
    // The id is the merge key — a new record typed over an existing id would
    // silently OVERWRITE that record (upsert semantics). Give the editor the
    // list so it can refuse.
    let existingIds: string[] = [];
    this.account$.pipe(take(1)).subscribe({
      next: (a) => {
        existingIds = this.servicesAsList(a?.data?.content)
          .map((x) => x._meta.id)
          .filter((id) => id && id !== svc._meta.id);
      },
    });
    const ref = this.dialogService.open(ContentSiteServiceComponent, {
      // Esc would close the editor and silently discard unsaved changes.
      closeOnEscape: false,
      header: isNew ? 'New service record' : `Edit ${svc.name}`,
      width: '54rem',
      baseZIndex: 11000,
      data: {
        service: svc,
        isNew,
        existingIds,
        // Editor-performed save: it closes only once the server confirms
        // (Save spins meanwhile), so a publish cannot race the write.
        save: (v: any) => this.accountService.patchContentItem('siteServices', v).pipe(take(1)),
      },
    });
    ref.onClose.subscribe({
      next: (result: { saved: boolean; service: any; persisted?: boolean } | undefined) => {
        if (!result?.saved) return;
        // The editor already wrote it (save callback above). This path remains
        // for a result without persisted — take(1), never takeUntil, since a
        // content write must outlive its dialog (see the menu list for the
        // EmptyError + aborted-save history).
        if (result.persisted) return;
        this.accountService
          .patchContentItem('siteServices', result.service)
          .pipe(take(1))
          .subscribe({ error: (err) => this.modalService.error(err?.error?.error || err) });
      },
    });
  }
}
